import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const NotificationSettings = ({ data, onUpdate }) => {
  const [localData, setLocalData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);

  const handleSave = () => {
    onUpdate(localData);
    setHasChanges(false);
  };

  const updateNotificationSetting = (type, setting, value) => {
    setLocalData(prev => ({
      ...prev,
      [type]: {
        ...prev?.[type],
        [setting]: value
      }
    }));
    setHasChanges(true);
  };

  const updateGeneralSetting = (setting, value) => {
    setLocalData(prev => ({
      ...prev,
      [setting]: value
    }));
    setHasChanges(true);
  };

  const updateQuietHours = (field, value) => {
    setLocalData(prev => ({
      ...prev,
      quietHours: {
        ...prev?.quietHours,
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const NotificationSection = ({ title, type, settings, icon, description }) => (
    <div className="bg-card rounded-lg p-6 border">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name={icon} size={20} className="mr-2" />
            {title}
          </h3>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
        </div>
      </div>
      
      <div className="space-y-4">
        {Object.entries(settings)?.map(([key, setting]) => (
          <div key={key} className="flex items-center justify-between py-2">
            <div>
              <label className="text-sm font-medium text-foreground">
                {setting?.label}
              </label>
              {setting?.description && (
                <p className="text-xs text-muted-foreground mt-1">
                  {setting?.description}
                </p>
              )}
            </div>
            
            <div className="flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={localData?.[type]?.[key] || false}
                  onChange={(e) => updateNotificationSetting(type, key, e?.target?.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const emailSettings = {
    bookingConfirmations: {
      label: 'Booking Confirmations',
      description: 'Get notified when your bookings are confirmed'
    },
    paymentReceipts: {
      label: 'Payment Receipts',
      description: 'Receive receipts for all payments'
    },
    tripReminders: {
      label: 'Trip Reminders',
      description: 'Reminders before your scheduled trips'
    },
    promotionalOffers: {
      label: 'Promotional Offers',
      description: 'Special deals and promotional content'
    },
    newsletterUpdates: {
      label: 'Newsletter Updates',
      description: 'Weekly travel tips and destination highlights'
    },
    priceDropAlerts: {
      label: 'Price Drop Alerts',
      description: 'Notifications when prices drop for saved tours'
    },
    weatherUpdates: {
      label: 'Weather Updates',
      description: 'Weather information for upcoming trips'
    }
  };

  const smsSettings = {
    bookingConfirmations: {
      label: 'Booking Confirmations',
      description: 'SMS confirmation for bookings'
    },
    paymentReceipts: {
      label: 'Payment Receipts',
      description: 'SMS receipts for payments'
    },
    tripReminders: {
      label: 'Trip Reminders',
      description: '24-hour trip reminders'
    },
    emergencyAlerts: {
      label: 'Emergency Alerts',
      description: 'Critical safety and emergency notifications'
    },
    lastMinuteDeals: {
      label: 'Last Minute Deals',
      description: 'Time-sensitive offers and deals'
    }
  };

  const pushSettings = {
    bookingUpdates: {
      label: 'Booking Updates',
      description: 'Real-time booking status changes'
    },
    checkInReminders: {
      label: 'Check-in Reminders',
      description: 'Reminders to check-in for activities'
    },
    localRecommendations: {
      label: 'Local Recommendations',
      description: 'Suggestions based on your location'
    },
    safetyAlerts: {
      label: 'Safety Alerts',
      description: 'Important safety information for your destination'
    },
    promotionalOffers: {
      label: 'Promotional Push',
      description: 'Push notifications for special offers'
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Notification Settings</h2>
          <p className="text-muted-foreground mt-1">
            Control how and when you receive notifications
          </p>
        </div>
        
        {hasChanges && (
          <Button
            onClick={handleSave}
            iconName="Save"
            size="sm"
          >
            Save Changes
          </Button>
        )}
      </div>
      {/* Notification Frequency */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Clock" size={20} className="mr-2" />
          Notification Frequency
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              How often would you like to receive notifications?
            </label>
            <select
              value={localData?.frequency || 'immediate'}
              onChange={(e) => updateGeneralSetting('frequency', e?.target?.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="immediate">Immediate</option>
              <option value="hourly">Hourly Digest</option>
              <option value="daily">Daily Digest</option>
              <option value="weekly">Weekly Digest</option>
            </select>
          </div>
        </div>
      </div>
      {/* Quiet Hours */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Moon" size={20} className="mr-2" />
          Quiet Hours
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground">
                Enable Quiet Hours
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                Pause non-urgent notifications during specified hours
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={localData?.quietHours?.enabled || false}
                onChange={(e) => updateQuietHours('enabled', e?.target?.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
          
          {localData?.quietHours?.enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Start Time"
                type="time"
                value={localData?.quietHours?.startTime || '22:00'}
                onChange={(e) => updateQuietHours('startTime', e?.target?.value)}
              />
              
              <Input
                label="End Time"
                type="time"
                value={localData?.quietHours?.endTime || '08:00'}
                onChange={(e) => updateQuietHours('endTime', e?.target?.value)}
              />
            </div>
          )}
        </div>
      </div>
      {/* Email Notifications */}
      <NotificationSection
        title="Email Notifications"
        type="emailNotifications"
        settings={emailSettings}
        icon="Mail"
        description="Manage your email notification preferences"
      />
      {/* SMS Notifications */}
      <NotificationSection
        title="SMS Notifications"
        type="smsNotifications"
        settings={smsSettings}
        icon="MessageSquare"
        description="Control SMS notifications (carrier charges may apply)"
      />
      {/* Push Notifications */}
      <NotificationSection
        title="Push Notifications"
        type="pushNotifications"
        settings={pushSettings}
        icon="Smartphone"
        description="Manage mobile app push notifications"
      />
      {/* Notification Preview */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Eye" size={20} className="mr-2" />
          Notification Preview
        </h3>
        
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
              <div>
                <p className="text-sm font-medium text-foreground">Booking Confirmed</p>
                <p className="text-xs text-muted-foreground">Your Bali Adventure trip has been confirmed for Dec 15-22</p>
                <p className="text-xs text-muted-foreground mt-1">Just now</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 rounded-full bg-accent mt-2"></div>
              <div>
                <p className="text-sm font-medium text-foreground">Price Drop Alert</p>
                <p className="text-xs text-muted-foreground">Santorini Sunset Tour is now $40 less! Book now to save.</p>
                <p className="text-xs text-muted-foreground mt-1">2 hours ago</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;